/* ═══════════════════════════════════════════════════════════════
   PAWSY PARASITE QUIZ ENGINE
   Adaptive symptom assessment w/ branching, loading, dynamic results
   Mounts on #pawsy-quiz-app  |  Config read from window.PAWSY_QUIZ_CONFIG
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var ROOT = document.getElementById('pawsy-quiz-app');
  if (!ROOT) return;

  var CFG = window.PAWSY_QUIZ_CONFIG || {};
  var T = CFG.text || {};
  var LOGO = CFG.logoUrl || '';
  var LOGO_TEXT = CFG.logoText || 'Pawsy';
  var PRODUCT_URL = CFG.productUrl || '/products/pawsy-parasite-cleanse-drops';
  var KLAVIYO_LIST = CFG.klaviyoListId || '';
  var STORE_KEY = 'pawsy_quiz_state_v1';

  function t(path, fallback) {
    var parts = path.split('.');
    var cur = T;
    for (var i = 0; i < parts.length; i++) {
      if (!cur || typeof cur !== 'object') return fallback;
      cur = cur[parts[i]];
    }
    return (cur === undefined || cur === null || cur === '') ? fallback : cur;
  }
  function tpl(s) {
    return String(s || '').replace(/\{dogName\}/g, dn()).replace(/\{DogName\}/g, dn());
  }
  /* For templates that are injected as raw HTML (merchant richtext):
     the user-typed dog name gets escaped, the merchant markup stays. */
  function tplHtml(s) {
    return String(s || '').replace(/\{dogName\}/gi, escapeHtml(dn()));
  }

  /* ── STATE ── */
  var state = {
    dogName: '',
    dogAge: '',
    dogSize: '',
    exposure: '',
    primaryConcerns: [],
    skinSymptoms: [],
    behaviorSymptoms: [],
    digestiveSymptoms: [],
    medications: [],
    medicationEffectiveness: '',
    vetTested: '',
    urgency: '',
    totalScore: 0,
    clusters: { skin: 0, behavioral: 0, digestive: 0, senior: 0, medication: 0, exposure: 0 },
    primaryCluster: '',
    riskTier: '',
    symptomCount: 0,
    screenHistory: [],
    startedAt: Date.now()
  };

  /* ── QUESTION DATA ── */
  var QUESTIONS = {
    q1: {
      type: 'text',
      key: 'dogName',
      question: "What's your dog's name?",
      sub: "We'll personalize your results.",
      placeholder: 'e.g. Bella'
    },
    q2: {
      type: 'single',
      key: 'dogAge',
      question: function(){ return 'How old is ' + dn() + '?'; },
      options: [
        { v: 'under-2', l: 'Under 2 years old', s: 0 },
        { v: '2-4', l: '2–4 years old', s: 5 },
        { v: '5-7', l: '5–7 years old', s: 10 },
        { v: '8-10', l: '8–10 years old', s: 15, c: ['senior'] },
        { v: '11+', l: '11+ years old', s: 20, c: ['senior'] }
      ]
    },
    q3: {
      type: 'single',
      key: 'dogSize',
      question: function(){ return 'What size is ' + dn() + '?'; },
      options: [
        { v: 'small', l: 'Small (under 25 lbs)', s: 5 },
        { v: 'medium', l: 'Medium (25–75 lbs)', s: 5 },
        { v: 'large', l: 'Large (75+ lbs)', s: 5 }
      ]
    },
    q4: {
      type: 'single',
      key: 'exposure',
      question: function(){ return 'In a typical month, how often is ' + dn() + ' exposed to any of the following?'; },
      sub: 'Dog parks, daycare, other dogs, communal water bowls, grassy public areas',
      options: [
        { v: 'rare', l: 'Rarely or never', s: 0 },
        { v: 'monthly', l: 'A few times a month', s: 10 },
        { v: 'weekly', l: 'Weekly', s: 20, c: ['exposure'] },
        { v: 'daily', l: 'Several times a week or daily', s: 30, c: ['exposure'] }
      ]
    },
    q5: {
      type: 'multi',
      key: 'primaryConcerns',
      question: function(){ return "What's your biggest concern with " + dn() + ' right now?'; },
      sub: "Select all that apply. We'll ask specific follow-up questions.",
      min: 1,
      options: [
        { v: 'skin', l: 'Skin, coat, or allergy issues', s: 5, c: ['skin'] },
        { v: 'behavior', l: 'Behavioral issues (anxiety, reactivity, restlessness)', s: 5, c: ['behavioral'] },
        { v: 'digestive', l: 'Digestive issues (scooting, upset stomach, picky eating)', s: 5, c: ['digestive'] },
        { v: 'urinary', l: 'Indoor accidents or urinary issues', s: 5, c: ['digestive'] },
        { v: 'sleep', l: 'Sleep disturbances or nighttime pacing', s: 5, c: ['behavioral'] },
        { v: 'decline', l: 'General decline — just not themselves anymore', s: 5, c: ['senior'] }
      ]
    },
    q6: {
      type: 'multi',
      key: 'skinSymptoms',
      question: function(){ return 'Which of these has ' + dn() + ' experienced in the past 3 months?'; },
      allowNone: true,
      options: [
        { v: 'paw-licking', l: 'Obsessive paw licking or chewing', s: 8, c: ['skin'] },
        { v: 'hot-spots', l: 'Hot spots, bald patches, or chronic itching', s: 8 },
        { v: 'ear-infections', l: 'Recurring ear infections', s: 6 },
        { v: 'red-belly', l: 'Red or inflamed belly/inner thighs', s: 6 },
        { v: 'dull-coat', l: "Dull or patchy coat no food change fixes", s: 6 },
        { v: 'none', l: 'None of the above', s: 0, none: true }
      ]
    },
    q7: {
      type: 'multi',
      key: 'behaviorSymptoms',
      question: function(){ return 'Have you noticed any of these behaviors in ' + dn() + '?'; },
      allowNone: true,
      options: [
        { v: 'restless-sleep', l: 'Restless sleep or 3AM pacing', s: 8, c: ['behavioral'] },
        { v: 'reactivity', l: 'Increased reactivity on walks toward other dogs', s: 8 },
        { v: 'anxiety', l: "Anxiety in situations that didn't used to bother them", s: 6 },
        { v: 'destructive', l: "Destructive behavior or can't settle", s: 6 },
        { v: 'not-themselves', l: '"Acting not themselves" in ways you can\'t explain', s: 6 },
        { v: 'none', l: 'None of the above', s: 0, none: true }
      ]
    },
    q8: {
      type: 'multi',
      key: 'digestiveSymptoms',
      question: function(){ return 'Have you seen any of these in ' + dn() + '?'; },
      allowNone: true,
      options: [
        { v: 'scooting', l: 'Scooting or butt-dragging across the floor', s: 8, c: ['digestive'] },
        { v: 'grass-eating', l: 'Obsessive grass eating on walks', s: 8 },
        { v: 'indoor-accidents', l: 'Indoor accidents in a previously house-trained dog', s: 8 },
        { v: 'picky-eating', l: 'Picky eating or walking away from meals', s: 6 },
        { v: 'loose-stools', l: 'Loose stools, bad breath, or occasional vomiting', s: 6 },
        { v: 'none', l: 'None of the above', s: 0, none: true }
      ]
    },
    q9: {
      type: 'multi',
      key: 'medications',
      question: function(){ return 'Is ' + dn() + ' currently on — or has ' + dn() + ' been on — any of these?'; },
      allowNone: true,
      options: [
        { v: 'apoquel', l: 'Apoquel', s: 15, c: ['medication'] },
        { v: 'cytopoint', l: 'Cytopoint injections', s: 15, c: ['medication'] },
        { v: 'rx-food', l: 'Prescription hypoallergenic food', s: 10, c: ['medication'] },
        { v: 'antibiotics', l: 'Long-term antibiotics', s: 10, c: ['medication'] },
        { v: 'anxiety-meds', l: 'Anti-anxiety medication (trazodone, fluoxetine, etc.)', s: 10, c: ['medication'] },
        { v: 'none', l: 'None of the above', s: 0, none: true }
      ]
    },
    q10: {
      type: 'single',
      key: 'medicationEffectiveness',
      question: function(){ return 'How well do those medications actually control ' + dn() + "'s symptoms?"; },
      options: [
        { v: 'complete', l: 'Completely — symptoms are fully resolved', s: 0 },
        { v: 'mostly', l: 'Mostly — some breakthrough symptoms but manageable', s: 10 },
        { v: 'partial', l: 'Partially — symptoms keep coming back', s: 20 },
        { v: 'stopped', l: 'They worked at first but stopped working', s: 25 },
        { v: 'stacking', l: "Not really — we're stacking more meds over time", s: 30 }
      ]
    },
    q11: {
      type: 'single',
      key: 'vetTested',
      question: function(){ return 'Has your vet ever tested ' + dn() + ' specifically for parasites?'; },
      options: [
        { v: 'positive', l: 'Yes, and they tested positive — we treated them', s: 5 },
        { v: 'negative', l: 'Yes, but the test came back negative', s: 10 },
        { v: 'mentioned', l: 'My vet mentioned it but never tested', s: 15 },
        { v: 'never', l: 'No, never been mentioned or tested', s: 20 },
        { v: 'unsure', l: "I'm not sure", s: 15 }
      ]
    },
    q12: {
      type: 'single',
      key: 'urgency',
      question: function(){ return 'If ' + dn() + ' was carrying parasites right now, how important would it be to address it quickly?'; },
      options: [
        { v: 'extreme', l: 'Extremely important — I want to help them now', s: 0 },
        { v: 'very', l: "Very important — I'm ready to do something about it", s: 0 },
        { v: 'learn', l: "Important, but I'd like to learn more first", s: 0 },
        { v: 'research', l: "I'm just researching for now", s: 0 }
      ]
    }
  };

  /* ── PER-QUESTION VET COMMENTARY (Dr. Kovač) ──
     Reassuring, expert microcopy shown under each question — mirrors the
     "vet card" pattern users expect from a clinical assessment funnel. */
  var VET = {
    q1: "I'll use your dog's name to personalise this whole assessment. It only takes about 60 seconds.",
    q2: "Age matters here — subclinical parasite loads build slowly, so older dogs often show the clearest patterns.",
    q3: "Knowing {dogName}'s size lets me recommend the right dosing range further on.",
    q4: "Most parasites spread through grass, shared water bowls and other dogs. Exposure is the single biggest risk factor I assess.",
    q5: "There are no wrong answers — whatever you pick, I'll ask the right follow-up questions for {dogName}.",
    q6: "Itchy paws and hot spots are often the gut talking, not true allergies. This helps me see the pattern.",
    q7: "About 90% of a dog's serotonin is made in the gut — which is why gut issues so often surface as behaviour.",
    q8: "These are the signs I see most with subclinical parasites. Be honest here — it helps me help {dogName}.",
    q9: "Knowing what {dogName} has already tried tells me how long the root cause may have been masked.",
    q10: "When medications work and then stop, it usually means the symptom was suppressed while the cause kept progressing.",
    q11: "Standard fecal tests miss up to 75% of cases, so a negative result doesn't always mean all-clear.",
    q12: "No pressure at all — this just helps me frame the right next step for you and {dogName}.",
    loading: "I'm matching {dogName}'s answers against the clinical indicators I use in my own practice. Hang tight."
  };

  var INFO_SCREENS = {
    info1: {
      eyebrow: 'Exposure Reality',
      title: "The Hidden Parasite Epidemic Nobody's Talking About",
      stat: '85%',
      statCap: 'of U.S. dog parks tested positive for intestinal parasites in the most recent large-scale surveillance study (DOGPARCS 2020)',
      body: '<p>Canine hookworm infections alone have increased <strong>45% since 2012</strong> according to CAPC surveillance data. Parasites spread through grass, shared water bowls, other dogs\' waste, and even tracked in on paws.</p>' +
            '<p>Once inside your dog, most of them don\'t cause the visible worms you\'re used to hearing about. They\'re microscopic. Subclinical. They quietly damage the gut lining for months or years before symptoms become obvious enough for most owners to connect the dots.</p>' +
            '<p>Your vet may have never mentioned them because American veterinary schools don\'t routinely train for subclinical parasite detection in adult dogs. It\'s why we built this assessment.</p>',
      cta: 'Continue →'
    },
    info2: {
      eyebrow: 'The Gut-Brain Connection',
      title: 'Why Symptoms Like These Are Almost Always Connected',
      stat: '90%',
      statCap: "of your dog's serotonin is produced in the gut — not the brain",
      body: '<p>This is the key insight most owners never hear from their vet.</p>' +
            '<p>When parasites colonize the gut, they damage the intestinal lining. That damage disrupts the production of serotonin, the neurotransmitter responsible for calm, regulated behavior. It\'s why dogs with chronic gut issues so often have chronic behavioral issues too — the reactivity, the 3AM pacing, the anxiety that no amount of training resolves.</p>' +
            '<p>The same damage causes immune system leakage, which shows up as chronic skin problems, ear infections, and "allergies" that don\'t respond to medication. The skin, the behavior, and the digestion aren\'t separate problems. They\'re all downstream of the same broken gut.</p>' +
            '<p>This is why medications like Apoquel and Cytopoint stop working over time. They suppress the symptom. The root cause keeps getting worse.</p>',
      cta: 'Continue →'
    },
    info3: {
      eyebrow: 'Why Standard Tests Fail',
      title: 'Why Most Dogs With Parasites Test "Negative"',
      stat: '75%',
      statCap: 'of Giardia cases are missed by the standard fecal flotation test most vets run',
      body: '<p>The standard fecal flotation misses up to <strong>50% of whipworm infections</strong> and up to <strong>75% of Giardia cases</strong>. The reason is simple: parasites don\'t shed eggs consistently. A single stool sample checked on a single day is statistically likely to miss the infection even if it\'s present.</p>' +
            '<p>But because the test came back negative, most vets close the conversation. They diagnose allergies, behavioral issues, or "just how your dog is." They write a prescription for the symptom and move on.</p>' +
            '<p>PCR tests that detect parasitic DNA directly are 5–10x more accurate — but most general practice vets don\'t order them unless pushed. And most owners don\'t know to ask.</p>' +
            '<p>This assessment is designed to surface the symptom pattern your vet\'s fecal test may have missed.</p>',
      cta: 'See My Results →'
    }
  };

  /* Screen order with branching. Pure question funnel (PureBob-style) —
     the info interstitials are gone; social proof is the single
     interstitial, right before the results are "prepared". */
  var FLOW = ['q1','q2','q3','q4','q5','q6','q7','q8','q9','q10','q11','q12','social','email','loading','results'];

  /* ── HELPERS ── */
  function dn() { return state.dogName || 'your dog'; }
  /* Clean rounded paw mark — reused for loading fallback + decorative bits */
  function pawSvg(fill) {
    fill = fill || 'currentColor';
    return '<svg viewBox="0 0 64 64" fill="' + fill + '" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">' +
      '<ellipse cx="20" cy="20" rx="6.5" ry="9"/>' +
      '<ellipse cx="44" cy="20" rx="6.5" ry="9"/>' +
      '<ellipse cx="9" cy="34" rx="5.5" ry="7.5"/>' +
      '<ellipse cx="55" cy="34" rx="5.5" ry="7.5"/>' +
      '<path d="M32 30c-8 0-14 6.2-14 13.2 0 5.3 4 8.8 9.2 8.8 2.4 0 3.6-1 4.8-1s2.4 1 4.8 1c5.2 0 9.2-3.5 9.2-8.8C46 36.2 40 30 32 30z"/>' +
    '</svg>';
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function(m){
      return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m];
    });
  }
  function save() {
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ state: state, savedAt: Date.now() })); } catch(e){}
  }
  function load() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.savedAt) return null;
      if (Date.now() - parsed.savedAt > 24 * 60 * 60 * 1000) return null;
      return parsed.state;
    } catch(e){ return null; }
  }
  function clearSaved() {
    try { localStorage.removeItem(STORE_KEY); } catch(e){}
  }

  function track(event, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, payload || {}));
  }

  function quizDose() {
    return { small: '0.5–1 ml', medium: '1–2 ml', large: '3 ml' }[state.dogSize] || '';
  }
  function quizSymptomLabels() {
    return selectedSymptomsList().join('; ');
  }

  /* Profile properties use universal, human-readable names (Dog Name,
     Dog Size, …) so every funnel/form/flow can reuse them — only truly
     quiz-specific metrics carry a quiz-flavoured name. */
  function klaviyoProps() {
    return {
      'Dog Name': state.dogName || '',
      'Dog Size': state.dogSize || '',
      'Dog Symptoms': quizSymptomLabels(),
      'Daily Dose': quizDose(),
      'Parasite Risk Tier': state.riskTier || '',
      'Primary Symptom Cluster': state.primaryCluster || '',
      'Parasite Quiz Score': state.totalScore || 0,
      'Symptom Count': state.symptomCount || 0,
      'Recommended Bundle': recommendedBundle()
    };
  }

  /* Klaviyo public key (company id) — parsed from the onsite script tag
     so we never hardcode it in the theme. */
  function klaviyoCompanyId() {
    var scripts = document.querySelectorAll('script[src*="klaviyo"]');
    for (var i = 0; i < scripts.length; i++) {
      var m = (scripts[i].src || '').match(/company_id=([A-Za-z0-9]+)/);
      if (m) return m[1];
    }
    return '';
  }

  function klaviyoIdentify(extra) {
    var props = klaviyoProps();
    if (extra) Object.assign(props, extra);
    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      try { window.klaviyo.identify(props); return; } catch (e) {}
    }
    if (typeof window._learnq !== 'undefined') {
      window._learnq.push(['identify', props]);
    }
  }

  /* Subscribe an email to the configured Klaviyo list with all profile
     properties. Uses the modern Client API (CORS-enabled, actually
     persists custom properties — the legacy kmail-lists endpoint drops
     them). Falls back to the legacy endpoint if no public key found. */
  function klaviyoSubscribe(email) {
    if (!KLAVIYO_LIST || !email) return;
    var companyId = klaviyoCompanyId();
    if (companyId) {
      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'parasite-quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: klaviyoProps()
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: KLAVIYO_LIST } }
          }
        }
      };
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify(payload)
      }).catch(function(){});
      return;
    }
    /* Legacy fallback (no custom props survive here, but consent does) */
    var props = { g: KLAVIYO_LIST, email: email, '$source': 'parasite-quiz' };
    var body = Object.keys(props).map(function(k){
      return encodeURIComponent(k) + '=' + encodeURIComponent(props[k]);
    }).join('&');
    fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body
    }).catch(function(){});
  }

  function applyAnswer(key, optOrOpts) {
    if (Array.isArray(optOrOpts)) {
      optOrOpts.forEach(addScoreClusters);
    } else if (optOrOpts) {
      addScoreClusters(optOrOpts);
    }
    recomputeDerived();
    save();
  }

  function addScoreClusters(opt) {
    if (!opt) return;
    state.totalScore += opt.s || 0;
    if (opt.c && opt.c.length) {
      opt.c.forEach(function(c){
        if (c === 'senior') {
          if (seniorAge()) state.clusters.senior += 2;
        } else {
          state.clusters[c] = (state.clusters[c] || 0) + 2;
        }
      });
    }
  }

  function seniorAge() {
    return state.dogAge === '8-10' || state.dogAge === '11+';
  }

  function recomputeDerived() {
    /* Reset and recompute from snapshots for safety */
    var total = 0;
    var clusters = { skin: 0, behavioral: 0, digestive: 0, senior: 0, medication: 0, exposure: 0 };

    function apply(q, val) {
      if (!q || !val) return;
      if (q.type === 'multi') {
        (val || []).forEach(function(v){
          var o = q.options.find(function(x){ return x.v === v; });
          if (!o) return;
          total += o.s || 0;
          (o.c || []).forEach(function(c){
            if (c === 'senior') { if (seniorAge()) clusters.senior += 2; }
            else clusters[c] = (clusters[c] || 0) + 2;
          });
        });
      } else {
        var o2 = q.options && q.options.find(function(x){ return x.v === val; });
        if (!o2) return;
        total += o2.s || 0;
        (o2.c || []).forEach(function(c){
          if (c === 'senior') { if (seniorAge()) clusters.senior += 2; }
          else clusters[c] = (clusters[c] || 0) + 2;
        });
      }
    }

    apply(QUESTIONS.q2, state.dogAge);
    apply(QUESTIONS.q3, state.dogSize);
    apply(QUESTIONS.q4, state.exposure);
    apply(QUESTIONS.q5, state.primaryConcerns);
    apply(QUESTIONS.q6, state.skinSymptoms);
    apply(QUESTIONS.q7, state.behaviorSymptoms);
    apply(QUESTIONS.q8, state.digestiveSymptoms);
    apply(QUESTIONS.q9, state.medications);
    apply(QUESTIONS.q10, state.medicationEffectiveness);
    apply(QUESTIONS.q11, state.vetTested);

    state.totalScore = total;
    state.clusters = clusters;

    /* Primary cluster */
    var best = ''; var bestN = 0;
    Object.keys(clusters).forEach(function(k){
      if (clusters[k] > bestN) { bestN = clusters[k]; best = k; }
    });
    state.primaryCluster = bestN === 0 ? 'mixed' : best;

    /* Risk tier */
    if (total >= 46) state.riskTier = 'high';
    else if (total >= 21) state.riskTier = 'moderate';
    else state.riskTier = 'low';

    /* Symptom count — unique indicators hit */
    var count = 0;
    count += (state.skinSymptoms || []).filter(function(v){ return v !== 'none'; }).length;
    count += (state.behaviorSymptoms || []).filter(function(v){ return v !== 'none'; }).length;
    count += (state.digestiveSymptoms || []).filter(function(v){ return v !== 'none'; }).length;
    state.symptomCount = Math.min(count, 8);
  }

  function recommendedBundle() {
    if (state.riskTier === 'high') return 'bundle_3_2';
    return 'bundle_2_1';
  }

  /* ── BRANCHING ── */
  function shouldShow(screenId) {
    if (screenId === 'q6') {
      return state.primaryConcerns.indexOf('skin') !== -1;
    }
    if (screenId === 'q7') {
      return state.primaryConcerns.indexOf('behavior') !== -1 ||
             state.primaryConcerns.indexOf('sleep') !== -1;
    }
    if (screenId === 'q8') {
      return state.primaryConcerns.indexOf('digestive') !== -1 ||
             state.primaryConcerns.indexOf('urinary') !== -1;
    }
    if (screenId === 'q10') {
      var meds = state.medications || [];
      return meds.length > 0 && meds.indexOf('none') === -1;
    }
    return true;
  }

  function getNext(currentId) {
    var idx = FLOW.indexOf(currentId);
    for (var i = idx + 1; i < FLOW.length; i++) {
      if (shouldShow(FLOW[i])) return FLOW[i];
    }
    return null;
  }

  function getPrev(currentId) {
    if (state.screenHistory.length <= 1) return null;
    state.screenHistory.pop();
    return state.screenHistory[state.screenHistory.length - 1];
  }

  /* ── RENDERING ── */
  var SCREEN_EL = null;
  var PROGRESS_EL = null;
  var HEADER_BACK = null;
  var HEADER_PROG = null;
  var currentScreenId = null;
  var questionCounter = 0;
  var totalQuestions = 12;

  function renderShell() {
    ROOT.innerHTML = [
      '<div class="plq-header">',
        '<button class="plq-header-back" aria-label="Go back" type="button"><span aria-hidden="true">←</span> Back</button>',
        LOGO ? '<img class="plq-header-logo" src="' + escapeHtml(LOGO) + '" alt="Pawsy"/>' : '<span class="plq-header-logo-text">' + escapeHtml(LOGO_TEXT) + '</span>',
        '<span class="plq-header-progress"></span>',
      '</div>',
      '<div class="plq-bar" role="progressbar" aria-hidden="true"></div>',
      '<div class="plq-screen-host"></div>'
    ].join('');
    PROGRESS_EL = ROOT.querySelector('.plq-bar');
    HEADER_BACK = ROOT.querySelector('.plq-header-back');
    HEADER_PROG = ROOT.querySelector('.plq-header-progress');
    SCREEN_EL = ROOT.querySelector('.plq-screen-host');
    HEADER_BACK.addEventListener('click', onBack);
  }

  /* Discrete segmented progress bar (PureBob style): one segment per shown
     question; `shown` segments filled, the current one highlighted. */
  function setSegments(shown, total) {
    if (!PROGRESS_EL) return;
    total = Math.max(total || 0, 1);
    var html = '';
    for (var i = 1; i <= total; i++) {
      var cls = 'plq-seg';
      if (i < shown) cls += ' plq-seg-done';
      else if (i === shown) cls += ' plq-seg-now';
      html += '<span class="' + cls + '"></span>';
    }
    PROGRESS_EL.innerHTML = html;
    PROGRESS_EL.classList.remove('plq-hidden');
  }
  function fillAllSegments() {
    if (!PROGRESS_EL) return;
    var segs = PROGRESS_EL.querySelectorAll('.plq-seg');
    if (!segs.length) { setSegments(1, 1); segs = PROGRESS_EL.querySelectorAll('.plq-seg'); }
    segs.forEach(function(s){ s.className = 'plq-seg plq-seg-done'; });
  }

  function updateHeader(screenId) {
    var isQ = /^q\d+$/.test(screenId);
    if (isQ) {
      /* question number (count only shown question slots out of total) */
      var countable = 0, shown = 0;
      for (var i = 0; i < FLOW.length; i++) {
        var id = FLOW[i];
        if (!/^q\d+$/.test(id)) continue;
        if (!shouldShow(id)) continue;
        countable++;
        if (id === screenId) shown = countable;
      }
      HEADER_PROG.textContent = shown + ' / ' + countable;
      HEADER_PROG.classList.remove('plq-hidden');
      setSegments(shown, countable);
    } else if (screenId === 'results' || screenId === 'loading' || screenId === 'intro') {
      HEADER_PROG.classList.add('plq-hidden');
      if (PROGRESS_EL) PROGRESS_EL.classList.add('plq-hidden');
    } else {
      /* info / social interstitials: hide counter, keep bar at last fill */
      HEADER_PROG.classList.add('plq-hidden');
    }
    /* Back button visibility */
    var canBack = state.screenHistory.length > 1 && screenId !== 'loading' && screenId !== 'results';
    HEADER_BACK.classList.toggle('plq-hidden', !canBack);
  }

  function go(screenId, opts) {
    opts = opts || {};
    if (!screenId) return;
    if (!opts.skipHistory) {
      if (state.screenHistory[state.screenHistory.length - 1] !== screenId) {
        state.screenHistory.push(screenId);
      }
    }
    currentScreenId = screenId;
    renderScreen(screenId);
    updateHeader(screenId);
    if (!opts.replaceHash) {
      try { history.pushState({ screen: screenId }, '', '#' + screenId); } catch(e){}
    }
    scrollToQuiz();
    track('quiz_screen_view', { screen_id: screenId });
  }

  function onBack() {
    var prev = getPrev(currentScreenId);
    if (prev) {
      currentScreenId = prev;
      renderScreen(prev);
      updateHeader(prev);
      try { history.pushState({ screen: prev }, '', '#' + prev); } catch(e){}
      scrollToQuiz();
    }
  }

  function scrollToQuiz() {
    if (!ROOT) return;
    var rect = ROOT.getBoundingClientRect();
    var top = rect.top + (window.pageYOffset || document.documentElement.scrollTop || 0);
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function renderScreen(id) {
    if (id === 'results') {
      SCREEN_EL.className = 'plq-screen-host plq-results';
    } else {
      SCREEN_EL.className = 'plq-screen-host';
    }
    if (QUESTIONS[id]) {
      renderQuestion(id);
    } else if (INFO_SCREENS[id]) {
      renderInfo(id);
    } else if (id === 'social') {
      renderSocial();
    } else if (id === 'email') {
      renderEmailGate();
    } else if (id === 'loading') {
      renderLoading();
    } else if (id === 'results') {
      renderResults();
    }
  }

  function renderVetCard(id) {
    var raw = VET[id];
    if (!raw) return '';
    var name = t('doctor.name', 'Dr. Lena Kovač');
    var img = t('doctor.image', '');
    var avatar = img
      ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(name) + '"/>'
      : '<span>' + escapeHtml((name || 'Dr').charAt(0)) + '</span>';
    var star = '<svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 7.7l5.4-.8z"/></svg>';
    return '<div class="plq-vet">' +
      '<div class="plq-vet-avatar">' + avatar + '</div>' +
      '<div class="plq-vet-body">' +
        '<div class="plq-vet-name">' + escapeHtml(name) + ' ' + star + '</div>' +
        '<p class="plq-vet-text">' + escapeHtml(tpl(raw)) + '</p>' +
      '</div>' +
    '</div>';
  }

  function renderQuestion(id) {
    var q = QUESTIONS[id];
    var defaultQ = typeof q.question === 'function' ? q.question() : q.question;
    var defaultSub = q.sub ? (typeof q.sub === 'function' ? q.sub() : q.sub) : '';
    var questionText = tpl(t('questions.' + id + '.question', defaultQ));
    var sub = tpl(t('questions.' + id + '.sub', defaultSub));
    var html;
    /* Optional question image via CFG.text.questions.qX.image */
    var qImage = t('questions.' + id + '.image', '');

    if (q.type === 'text') {
      var current = state[q.key] || '';
      html = [
        '<section class="plq-screen plq-active"><div class="plq-container">',
          qImage ? '<div class="plq-qimg"><img src="' + escapeHtml(qImage) + '" alt=""/></div>' : '',
          '<h1 class="plq-question">' + escapeHtml(questionText) + '</h1>',
          sub ? '<p class="plq-sub">' + escapeHtml(sub) + '</p>' : '',
          '<input type="text" class="plq-input" id="plq-text-input" value="' + escapeHtml(current) + '" placeholder="' + escapeHtml(q.placeholder || '') + '" autocomplete="off" maxlength="30" aria-label="' + escapeHtml(questionText) + '"/>',
          renderVetCard(id),
          '<div class="plq-cta-wrap"><button type="button" class="plq-cta plq-cta-off" id="plq-next" aria-disabled="true">Next →</button></div>',
        '</div></section>'
      ].join('');
      SCREEN_EL.innerHTML = html;
      var input = document.getElementById('plq-text-input');
      var nextBtn = document.getElementById('plq-next');
      function validate() {
        var hasVal = !!input.value.trim();
        nextBtn.classList.toggle('plq-cta-off', !hasVal);
        nextBtn.setAttribute('aria-disabled', String(!hasVal));
      }
      input.addEventListener('input', validate);
      input.addEventListener('keydown', function(e){
        if (e.key === 'Enter' && input.value.trim()) { e.preventDefault(); nextBtn.click(); }
      });
      nextBtn.addEventListener('click', function(){
        if (!input.value.trim()) return;
        state.dogName = input.value.trim().slice(0, 30);
        save();
        track('quiz_question_answered', { question_id: id, answer: state.dogName });
        if (id === 'q1' && state.screenHistory.length === 1) track('quiz_start', {});
        go(getNext(id));
      });
      setTimeout(function(){ input.focus(); }, 350);
      validate();
      return;
    }

    /* single or multi */
    var isMulti = q.type === 'multi';
    var selected = isMulti ? (state[q.key] || []).slice() : (state[q.key] || '');
    var useGrid2 = q.options.length >= 4 && q.options.every(function(o){ return o.l.length < 40; });

    /* CTA is always visible for multi (disabled until valid); single
       auto-advances so it renders no CTA at all. */
    var initialValid = isMulti ? selected.length > 0 : !!selected;
    var offCtaClass = initialValid ? '' : ' plq-cta-off';

    html = [
      '<section class="plq-screen plq-active"><div class="plq-container">',
        qImage ? '<div class="plq-qimg"><img src="' + escapeHtml(qImage) + '" alt=""/></div>' : '',
        '<h1 class="plq-question">' + escapeHtml(questionText) + '</h1>',
        sub ? '<p class="plq-sub">' + escapeHtml(sub) + '</p>' : '',
        '<div class="plq-options' + (useGrid2 ? ' plq-grid2' : '') + '" role="' + (isMulti ? 'group' : 'radiogroup') + '" aria-label="' + escapeHtml(questionText) + '">',
          q.options.map(function(o){
            var sel = isMulti ? selected.indexOf(o.v) !== -1 : selected === o.v;
            return '<button type="button" class="plq-opt' + (sel ? ' plq-selected' : '') + '" data-v="' + escapeHtml(o.v) + '" role="' + (isMulti ? 'checkbox' : 'radio') + '" aria-checked="' + sel + '">' +
              '<span class="plq-opt-box ' + (isMulti ? 'plq-check' : 'plq-radio') + '" aria-hidden="true"></span>' +
              '<span class="plq-opt-label">' + escapeHtml(o.l) + '</span>' +
            '</button>';
          }).join(''),
        '</div>',
        renderVetCard(id),
        isMulti ? '<div class="plq-cta-wrap"><button type="button" class="plq-cta' + offCtaClass + '" id="plq-next" aria-disabled="' + (!initialValid) + '">Next →</button></div>' : '',
      '</div></section>'
    ].join('');
    SCREEN_EL.innerHTML = html;

    var opts = SCREEN_EL.querySelectorAll('.plq-opt');
    var nextBtn = document.getElementById('plq-next');

    function setMultiValid(valid) {
      if (!nextBtn) return;
      nextBtn.classList.toggle('plq-cta-off', !valid);
      nextBtn.setAttribute('aria-disabled', String(!valid));
    }

    opts.forEach(function(btn){
      btn.addEventListener('click', function(){
        var v = btn.dataset.v;
        var optDef = q.options.find(function(x){ return x.v === v; });
        if (isMulti) {
          if (optDef.none) {
            opts.forEach(function(b){ b.classList.remove('plq-selected'); b.setAttribute('aria-checked','false'); });
            btn.classList.add('plq-selected');
            btn.setAttribute('aria-checked','true');
            selected = ['none'];
          } else {
            var noneBtn = Array.from(opts).find(function(b){
              var def = q.options.find(function(x){ return x.v === b.dataset.v; });
              return def && def.none;
            });
            if (noneBtn) { noneBtn.classList.remove('plq-selected'); noneBtn.setAttribute('aria-checked','false'); }
            selected = selected.filter(function(x){ return x !== 'none'; });
            var i = selected.indexOf(v);
            if (i === -1) {
              selected.push(v);
              btn.classList.add('plq-selected');
              btn.setAttribute('aria-checked','true');
            } else {
              selected.splice(i, 1);
              btn.classList.remove('plq-selected');
              btn.setAttribute('aria-checked','false');
            }
          }
          var minMet = q.allowNone ? selected.length > 0 : selected.length >= (q.min || 1);
          setMultiValid(minMet);
        } else {
          opts.forEach(function(b){ b.classList.remove('plq-selected'); b.setAttribute('aria-checked','false'); });
          btn.classList.add('plq-selected');
          btn.setAttribute('aria-checked','true');
          selected = v;
          /* single auto-advances, no Next button to show */
          setTimeout(function(){ commitAnswer(); }, 300);
        }
      });
    });

    function commitAnswer() {
      if (isMulti) {
        var minMet = q.allowNone ? selected.length > 0 : selected.length >= (q.min || 1);
        if (!minMet) return;
      } else if (!selected) {
        return;
      }
      state[q.key] = isMulti ? selected.slice() : selected;
      recomputeDerived();
      save();
      track('quiz_question_answered', {
        question_id: id,
        answer: isMulti ? selected.join(',') : selected,
        current_score: state.totalScore,
        current_clusters: Object.keys(state.clusters).filter(function(k){ return state.clusters[k] > 0; }).join(',')
      });
      go(getNext(id));
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function(){ commitAnswer(); });
    }
  }

  function renderInfo(id) {
    var info = INFO_SCREENS[id];
    var eyebrow = tpl(t('info.' + id + '.eyebrow', info.eyebrow));
    var title = tpl(t('info.' + id + '.title', info.title));
    var stat = tpl(t('info.' + id + '.stat', info.stat));
    var statCap = tpl(t('info.' + id + '.statCap', info.statCap));
    var body = tplHtml(t('info.' + id + '.body', info.body));
    var cta = tpl(t('info.' + id + '.cta', info.cta));
    var image = t('info.' + id + '.image', '');
    SCREEN_EL.innerHTML = [
      '<section class="plq-screen plq-info-screen plq-active"><div class="plq-info-inner">',
        '<p class="plq-info-eyebrow">' + escapeHtml(eyebrow) + '</p>',
        image ? '<div class="plq-info-img"><img src="' + escapeHtml(image) + '" alt=""/></div>' : '',
        '<h2 class="plq-info-title">' + escapeHtml(title) + '</h2>',
        '<div class="plq-info-stat">' + escapeHtml(stat) + '</div>',
        '<p class="plq-info-stat-cap">' + escapeHtml(statCap) + '</p>',
        '<hr class="plq-info-divider"/>',
        '<div class="plq-info-body">' + body + '</div>',
        '<div class="plq-info-cta-wrap"><button type="button" class="plq-cta" id="plq-next">' + escapeHtml(cta) + '</button></div>',
      '</div></section>'
    ].join('');
    var shownAt = Date.now();
    document.getElementById('plq-next').addEventListener('click', function(){
      track('quiz_info_screen_viewed', { info_screen_id: id, time_on_screen: Math.round((Date.now() - shownAt) / 1000) });
      go(getNext(id));
    });
  }

  function renderSocial() {
    var title = tpl(t('social.title', '30,000+ Dog Owners Have Taken This Assessment'));
    var sub = tpl(t('social.sub', "Here's what they had to say after starting the protocol:"));
    var footer = tpl(t('social.footer', "You're almost done. {dogName}'s personalized results are being prepared."));
    var cta = tpl(t('social.cta', "See {dogName}'s Results →"));
    var testimonials = t('social.testimonials', [
      { q: '"Three weeks in and the scooting is completely gone. I wish I\'d taken this test two years ago."', a: 'Amanda, Golden Retriever, age 7' },
      { q: '"My dog was on Apoquel for 3 years. Within 6 weeks on the Pawsy protocol, he was off it completely. My vet was stunned."', a: 'Tyler, Labrador, age 6' },
      { q: '"She went from thrashing all night to sleeping through the night. I actually cried when I realized the pacing had stopped."', a: 'Rebecca, Cavalier King Charles Spaniel, age 4' }
    ]);
    SCREEN_EL.innerHTML = [
      '<section class="plq-screen plq-active"><div class="plq-container">',
        '<h2 class="plq-sp-title">' + escapeHtml(title) + '</h2>',
        '<p class="plq-sp-sub">' + escapeHtml(sub) + '</p>',
        '<div class="plq-sp-scroller"><div class="plq-sp-grid">',
          testimonials.map(function(tt){ return renderSP(tpl(tt.q), tpl(tt.a), tt.image || ''); }).join(''),
        '</div></div>',
        '<p class="plq-sp-footer">' + escapeHtml(footer) + '</p>',
        '<div class="plq-cta-wrap"><button type="button" class="plq-cta" id="plq-next">' + escapeHtml(cta) + '</button></div>',
      '</div></section>'
    ].join('');
    document.getElementById('plq-next').addEventListener('click', function(){ go(getNext('social')); });
  }

  function renderSP(quote, author, image) {
    return '<div class="plq-sp-card">' +
      (image ? '<div class="plq-sp-img"><img src="' + escapeHtml(image) + '" alt=""/></div>' : '') +
      '<div class="plq-sp-stars">★★★★★</div>' +
      '<p class="plq-sp-quote">' + escapeHtml(quote) + '</p>' +
      '<p class="plq-sp-author">— ' + escapeHtml(author) + '</p>' +
    '</div>';
  }

  /* ── EMAIL GATE (PureBob pattern): required email + consent before the
     results are "calculated". Subscribes to Klaviyo and moves to loading. */
  function renderEmailGate() {
    var timeline = t('results.timeline', []);
    var steps = [
      { img: (timeline[0] && timeline[0].image) || '', txt: 'You receive ' + dn() + '\'s personal assessment report' },
      { img: (timeline[1] && timeline[1].image) || '', txt: 'We match the protocol and dosing to ' + dn() + '\'s size' },
      { img: (timeline[2] && timeline[2].image) || '', txt: 'You choose ' + dn() + '\'s protocol package' }
    ];
    SCREEN_EL.innerHTML = [
      '<section class="plq-screen plq-active"><div class="plq-container">',
        '<h1 class="plq-question">Almost done!</h1>',
        '<p class="plq-sub">Where should we send ' + escapeHtml(dn()) + '\'s personalized parasite report?</p>',
        '<input type="email" class="plq-input" id="plq-gate-email" placeholder="' + escapeHtml(tpl(t('email.placeholder', 'you@example.com'))) + '" autocomplete="email" aria-label="Email address"/>',
        '<label class="plq-consent"><input type="checkbox" id="plq-gate-consent"/><span>I agree to the <a href="/policies/terms-of-service" target="_blank" rel="noopener">terms</a> and the <a href="/policies/privacy-policy" target="_blank" rel="noopener">privacy policy</a></span></label>',
        '<p class="plq-gate-nextlabel">What happens after this?</p>',
        '<div class="plq-gate-steps">',
          steps.map(function(s, i){
            return '<div class="plq-gate-step">' +
              (s.img ? '<div class="plq-gate-img"><span class="plq-gate-n">' + (i + 1) + '</span><img src="' + escapeHtml(s.img) + '" alt=""/></div>' : '<span class="plq-gate-n plq-gate-n-solo">' + (i + 1) + '</span>') +
              '<p>' + escapeHtml(s.txt) + '</p>' +
            '</div>';
          }).join(''),
        '</div>',
        '<div class="plq-cta-wrap"><button type="button" class="plq-cta plq-cta-off" id="plq-next" aria-disabled="true">' + escapeHtml('See ' + dn() + '\'s Results →') + '</button></div>',
      '</div></section>'
    ].join('');

    var emailInput = document.getElementById('plq-gate-email');
    var consent = document.getElementById('plq-gate-consent');
    var nextBtn = document.getElementById('plq-next');
    function valid() {
      return /.+@.+\..+/.test(emailInput.value.trim()) && consent.checked;
    }
    function refresh() {
      nextBtn.classList.toggle('plq-cta-off', !valid());
      nextBtn.setAttribute('aria-disabled', String(!valid()));
    }
    emailInput.addEventListener('input', refresh);
    consent.addEventListener('change', refresh);
    emailInput.addEventListener('keydown', function(e){
      if (e.key === 'Enter' && valid()) { e.preventDefault(); nextBtn.click(); }
    });
    nextBtn.addEventListener('click', function(){
      if (!valid()) return;
      var email = emailInput.value.trim();
      state.email = email;
      save();
      track('quiz_email_captured', { placement: 'gate' });
      klaviyoIdentify({ $email: email });
      klaviyoSubscribe(email);
      if (typeof window._learnq !== 'undefined') {
        window._learnq.push(['track', 'Quiz Completed', Object.assign({ email: email }, klaviyoProps())]);
      }
      go(getNext('email'));
    });
    setTimeout(function(){ emailInput.focus(); }, 350);
  }

  function optLabel(q, val) {
    if (!q || !q.options || !val) return '';
    var o = q.options.find(function(x){ return x.v === val; });
    return o ? o.l : '';
  }

  function renderLoading() {
    var loadingIcon = t('loading.icon', '') || CFG.productImage || '';
    var imgHtml = loadingIcon
      ? '<img src="' + escapeHtml(loadingIcon) + '" alt=""/>'
      : pawSvg('#377359');

    /* Floating result chips derived from the user's answers */
    var ageLbl = optLabel(QUESTIONS.q2, state.dogAge);
    var concernLbl = optLabel(QUESTIONS.q5, (state.primaryConcerns || [])[0]);
    if (concernLbl) concernLbl = concernLbl.split('(')[0].trim();
    var chips = [];
    /* Keep chip labels short so they never spill out of the pill; the
       txt span ellipsizes as a safety net. */
    if (concernLbl) concernLbl = concernLbl.split(',')[0].split(' issues')[0].trim();
    function lvChip(pos, label) {
      return '<span class="plq-lv-chip plq-lv-chip-' + pos + '"><span class="plq-lv-tick">✓</span><span class="plq-lv-txt">' + escapeHtml(label) + '</span></span>';
    }
    chips.push(lvChip('tl', dn()));
    if (ageLbl) chips.push(lvChip('tr', ageLbl.replace(' years old', 'y')));
    if (concernLbl) chips.push(lvChip('bl', concernLbl));

    SCREEN_EL.innerHTML = [
      '<section class="plq-screen plq-loading-screen plq-active"><div class="plq-loading-inner">',
        '<div class="plq-loading-visual">',
          '<div class="plq-loading-ring" id="plq-lring">',
            '<div class="plq-loading-ring-img">' + imgHtml + '</div>',
            '<span class="plq-loading-pct" id="plq-lpct">0%</span>',
          '</div>',
          chips.join(''),
        '</div>',
        '<h2 class="plq-loading-title">' + escapeHtml(tpl(t('loading.title', "Analyzing {dogName}'s Symptom Profile..."))) + '</h2>',
        '<p class="plq-loading-sub">' + escapeHtml(tpl(t('loading.sub', "One moment — building {dogName}'s personalized report."))) + '</p>',
        '<div class="plq-loading-card">',
          '<div class="plq-loading-steps" id="plq-lsteps">',
            [
              t('loading.step1', 'Evaluating your answers...'),
              t('loading.step2', 'Cross-referencing 8 clinical indicators...'),
              t('loading.step3', 'Comparing against 30,000+ assessed dogs...'),
              t('loading.step4', "Dr. Lena Kovač is preparing {dogName}'s recommendation...")
            ].map(function(line, i){
              return '<div class="plq-loading-step" data-idx="' + i + '"><span class="plq-loading-check">✓</span> ' + escapeHtml(tpl(line)) + '</div>';
            }).join(''),
          '</div>',
        '</div>',
        renderVetCard('loading'),
      '</div></section>'
    ].join('');

    recomputeDerived();
    track('quiz_completed', {
      total_score: state.totalScore,
      risk_tier: state.riskTier,
      primary_cluster: state.primaryCluster,
      symptom_count: state.symptomCount,
      recommended_bundle: recommendedBundle()
    });
    klaviyoIdentify();

    /* Progress ring + counting percentage (0→100 over ~5s). The ring fill
       is driven by the --p custom property (conic-gradient in CSS). */
    var ring = document.getElementById('plq-lring');
    var pctEl = document.getElementById('plq-lpct');
    var DURATION = 5000;
    var start = null;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      if (ring) ring.style.setProperty('--p', '100');
      if (pctEl) pctEl.textContent = '100%';
    } else {
      function tick(ts) {
        if (start === null) start = ts;
        var p = Math.min(1, (ts - start) / DURATION);
        var val = Math.round(p * 100);
        if (ring) ring.style.setProperty('--p', String(val));
        if (pctEl) pctEl.textContent = val + '%';
        if (p < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }
    var steps = SCREEN_EL.querySelectorAll('.plq-loading-step');
    var times = [200, 1400, 2800, 4100];
    times.forEach(function(t, i){
      setTimeout(function(){ steps[i] && steps[i].classList.add('plq-on'); }, t);
    });
    setTimeout(function(){ go('results'); }, 5000);
  }

  /* ── RESULTS CONTENT ── */
  function riskHeadline() {
    var tier = state.riskTier;
    var defaults = {
      low: "{dogName}'s Symptoms Are Within Early Range",
      moderate: "{dogName}'s Symptoms Show Clear Warning Signs",
      high: "{dogName}'s Symptoms Are Highly Consistent With Subclinical Parasite Load"
    };
    return escapeHtml(tpl(t('results.headline.' + tier, defaults[tier])));
  }

  function narrativeHtml() {
    var c = state.primaryCluster;
    var key = (c === 'senior' && seniorAge()) ? 'senior' :
              (c === 'skin' ? 'skin' :
              (c === 'behavioral' ? 'behavioral' :
              (c === 'digestive' ? 'digestive' : 'mixed')));
    var defaults = {
      senior: "<p>{dogName}'s age pattern is important here. Subclinical parasite loads often build up slowly over years, which is why senior dogs frequently show the most severe symptom clusters. Many of the changes attributed to \"just getting older\" — the slowing down, the picky eating, the nighttime restlessness, the dull coat — are actually 10+ years of accumulated gut damage from undiagnosed parasites.</p><p>The good news: senior dogs often show the most dramatic improvements on the Pawsy protocol because they have the most to gain from finally resolving what's been building for years.</p>",
      skin: "<p>This is a classic pattern. Dogs with compromised gut lining develop chronic immune dysregulation that shows up first on the skin — itchy paws, hot spots, recurring ear infections, a dull coat that no food change fixes. It's not allergies. It's the gut leaking inflammatory signals into the bloodstream because parasites have damaged the intestinal wall.</p><p>Apoquel and Cytopoint suppress the immune response that causes the scratching, but they don't fix the gut that's triggering it. That's why they work at first and then stop working. The root cause keeps getting worse underneath.</p>",
      behavioral: "<p>This is one of the most common patterns Dr. Kovač sees in her clinic — and one of the most misdiagnosed. Dogs with gut dysfunction can't regulate their nervous system properly because 90% of their serotonin is produced in the gut. When the gut is compromised, serotonin production collapses, and you see exactly what {dogName} is showing: 3AM pacing, reactivity, anxiety, inability to settle.</p><p>Training doesn't work because the biology underneath is broken. Behaviorists can't fix a nervous system that's being chemically disrupted from the gut. The behavior won't normalize until the gut does.</p>",
      digestive: "<p>{dogName}'s symptoms are textbook subclinical parasite presentation. Scooting that anal gland expression doesn't resolve. Indoor accidents in a house-trained dog. Obsessive grass eating. Picky eating. These aren't random — they all point to disrupted gut function caused by parasitic activity in the intestinal lining.</p><p>Most vets treat these symptoms separately with separate prescriptions. But they share one root cause, and when that cause is addressed, all of the symptoms typically resolve together within 3 weeks.</p>",
      mixed: "<p>{dogName} is showing symptoms across multiple body systems — skin, digestion, behavior. That's not coincidence. These are all downstream of the same broken gut. When the intestinal lining is compromised by parasitic activity, the effects cascade: immune system leakage shows up as skin issues, serotonin disruption shows up as behavioral issues, and gut inflammation shows up as digestive issues.</p><p>This is exactly the pattern Dr. Kovač's Pawsy protocol was designed to address — targeting the root cause that creates all these symptoms at once.</p>"
    };
    return tpl(t('results.narrative.' + key, defaults[key]));
  }

  function recommendationHtml() {
    var tier = state.riskTier;
    var defaults = {
      low: "Based on {dogName}'s symptom profile, I'd recommend the 21-day Pawsy Parasite Cleanse protocol followed by ongoing maintenance dosing 3 times per week. Even mild symptom clusters tend to progress over months and years as parasite loads accumulate. The sooner you address it, the less work the body has to do to recover.",
      moderate: "Based on {dogName}'s symptom profile, I'd strongly recommend running the full 21-day Pawsy Parasite Cleanse protocol followed by daily maintenance. This is the exact pattern I see most often in my clinic — symptoms that medications can't fully resolve, stacking over time. Addressing the root cause now typically resolves the cluster within 3–4 weeks.",
      high: "Based on {dogName}'s symptom profile, this is a high-intervention case. I'd recommend the full 21-day Pawsy Parasite Cleanse protocol plus extended maintenance, plus seasonal reset doses every 3 months. {dogName}'s symptoms have been compounding for a while, and full recovery may take 6–8 weeks rather than 3. But the pattern is extremely consistent with what I see resolve successfully in my clinic when owners commit to the full protocol."
    };
    return tpl(t('results.recommendation.' + tier, defaults[tier]));
  }

  function doseForSize() {
    if (state.dogSize === 'small') return { size: 'small', dose: '0.5–1 ml daily' };
    if (state.dogSize === 'medium') return { size: 'medium', dose: '1–2 ml daily' };
    return { size: 'large', dose: '3 ml daily' };
  }

  function selectedSymptomsList() {
    var labels = [];
    function push(q, vals) {
      (vals || []).forEach(function(v){
        if (v === 'none') return;
        var o = q.options.find(function(x){ return x.v === v; });
        if (o) labels.push(o.l);
      });
    }
    push(QUESTIONS.q6, state.skinSymptoms);
    push(QUESTIONS.q7, state.behaviorSymptoms);
    push(QUESTIONS.q8, state.digestiveSymptoms);
    return labels;
  }

  function riskTierPosition() {
    /* returns left% position of dot */
    if (state.riskTier === 'low') return 15;
    if (state.riskTier === 'moderate') return 50;
    return 88;
  }

  function bundleData() {
    /* Single product card — Kaching Bundles on the product page handles
       multi-bottle pricing. The quiz tier only affects the narrative
       copy above this card, not which product is recommended. */
    var product = CFG.product || {};
    var defaults = {
      title: 'Pawsy Parasite Cleanse Drops',
      subtitle: "Dr. Kovač's 6-herb liquid protocol",
      list: [
        'Covers the full 21-day cleanse protocol',
        'Veterinary-grade probiotic included',
        '98% liquid absorption vs 20% for chews',
        '90-day money-back guarantee'
      ]
    };
    return {
      id: 'parasite_cleanse_drops',
      title: product.title || defaults.title,
      subtitle: product.subtitle || defaults.subtitle,
      image: product.image || CFG.productImage || '',
      list: (product.list && product.list.length) ? product.list : defaults.list
    };
  }

  /* Severity meter — gradient track with an obvious pointer + tier pill */
  function severityMeterHtml(labels, idx) {
    var pos = riskTierPosition();
    var cur = labels[idx] || '';
    return '<div class="plq-sev">' +
      '<div class="plq-sev-pin" style="left:' + pos + '%">' +
        '<span class="plq-sev-pill">' + escapeHtml(cur) + ' RISK</span>' +
        '<span class="plq-sev-caret"></span>' +
      '</div>' +
      '<div class="plq-sev-track"></div>' +
      '<div class="plq-sev-labels">' +
        labels.map(function(l, i){ return '<span class="' + (i === idx ? 'plq-sev-on' : '') + '">' + escapeHtml(l) + '</span>'; }).join('') +
      '</div>' +
    '</div>';
  }

  /* Profile chips — grouped like a personalised intake summary so the
     reader instantly recognises their own answers (PureBob pattern) */
  function profileChipsHtml() {
    var profile = [];
    var age = optLabel(QUESTIONS.q2, state.dogAge);
    var size = optLabel(QUESTIONS.q3, state.dogSize);
    var exp = optLabel(QUESTIONS.q4, state.exposure);
    if (age) profile.push(age);
    if (size) profile.push(size.replace(/\s*\(.*\)/, '').trim());
    if (exp) profile.push(exp + ' exposure');
    var concerns = [];
    (state.primaryConcerns || []).forEach(function(v){
      var l = optLabel(QUESTIONS.q5, v);
      if (l) concerns.push(l.split('(')[0].trim());
    });
    function group(label, chips) {
      if (!chips.length) return '';
      return '<div class="plq-r-profile-group">' +
        '<p class="plq-r-profile-label">' + escapeHtml(label) + '</p>' +
        '<div class="plq-chips">' + chips.map(function(c){ return '<span class="plq-chip">' + escapeHtml(c) + '</span>'; }).join('') + '</div>' +
      '</div>';
    }
    var html = group(dn() + '\'s profile', profile) + group('Main concerns', concerns);
    return html ? '<div class="plq-r-profile">' + html + '</div>' : '';
  }

  /* What's in the box — 3 cards */
  function whatsInBoxHtml() {
    var items = t('results.box', [
      { image: CFG.productImage || '', title: 'Parasite Cleanse Drops', body: 'A full 21-day liquid course — Dr. Kovač\'s 6-herb formula, duck-flavored.' },
      { image: '', title: 'ml-Marked Pipette', body: 'Easy daily dosing straight onto food — no pills, no fuss.' },
      { image: '', title: '{dogName}\'s 21-Day Protocol', body: 'A personalized schedule + dosing chart matched to {dogName}\'s size.' }
    ]);
    if (!items || !items.length) return '';
    return '<section class="plq-results-section"><div class="plq-inner">' +
      '<p class="plq-r-eyebrow">' + escapeHtml(tpl(t('results.boxLabel', 'What\'s in the box'))) + '</p>' +
      '<h2 class="plq-r-h2">' + escapeHtml(tpl(t('results.boxTitle', 'Everything {dogName} needs to start'))) + '</h2>' +
      '<div class="plq-box-grid">' +
        items.map(function(it){
          return '<div class="plq-box-card">' +
            (it.image ? '<div class="plq-box-img"><img src="' + escapeHtml(it.image) + '" alt=""/></div>' : '<div class="plq-box-img plq-box-img-ph">' + pawSvg('#377359') + '</div>') +
            '<h4>' + escapeHtml(tpl(it.title || '')) + '</h4>' +
            '<p>' + escapeHtml(tpl(it.body || '')) + '</p>' +
          '</div>';
        }).join('') +
      '</div>' +
    '</div></section>';
  }

  /* Health signal tiles — one clean callout per concern the owner picked
     (PureBob's "Goed om te weten" pattern): icon + label + how we account
     for it. */
  function healthTilesHtml() {
    var tiles = [];
    var concerns = state.primaryConcerns || [];
    if (concerns.indexOf('skin') !== -1) {
      tiles.push({ label: 'Skin, coat & allergy signals', body: 'Itchy paws, hot spots and recurring ear infections usually trace back to a gut-immune imbalance rather than true allergies. The protocol targets that root cause — skin is often the first place you see change.' });
    }
    if (concerns.indexOf('behavior') !== -1 || concerns.indexOf('sleep') !== -1) {
      tiles.push({ label: 'Restlessness & reactivity', body: 'About 90% of a dog\'s serotonin is produced in the gut. When the gut is disrupted, sleep and behaviour suffer with it — calming the gut typically calms the dog.' });
    }
    if (concerns.indexOf('digestive') !== -1 || concerns.indexOf('urinary') !== -1) {
      tiles.push({ label: 'Digestive signals', body: 'Scooting, grass eating and loose stools point directly at gut irritation — the strongest indicator of subclinical parasite load, and the first cluster to resolve on the protocol.' });
    }
    if (seniorAge() || concerns.indexOf('decline') !== -1) {
      tiles.push({ label: 'Older dog', body: 'Parasite load builds up slowly over years, so older dogs often show the clearest symptom patterns — and the most visible improvement once the root cause is addressed.' });
    }
    if (!tiles.length) return '';
    return '<section class="plq-results-section"><div class="plq-inner">' +
      '<p class="plq-r-eyebrow">Good to know</p>' +
      '<h2 class="plq-r-h2">About ' + escapeHtml(dn()) + '\'s health</h2>' +
      '<p class="plq-r-secsub">Per selected signal, how the protocol takes it into account:</p>' +
      '<div class="plq-htiles">' +
        tiles.map(function(ti){
          return '<div class="plq-htile">' +
            '<span class="plq-htile-i" aria-hidden="true">i</span>' +
            '<div><p class="plq-htile-label">' + escapeHtml(ti.label) + '</p>' +
            '<p class="plq-htile-body">' + escapeHtml(ti.body) + '</p></div>' +
          '</div>';
        }).join('') +
      '</div>' +
    '</div></section>';
  }

  /* Why this protocol fits {dog} — plus-bullet recap built from answers */
  function whyPerfectHtml() {
    var bullets = [];
    var dose = doseForSize();
    var age = optLabel(QUESTIONS.q2, state.dogAge);
    if (age) bullets.push('Dosing and duration matched to a ' + age.toLowerCase().replace(' years old', '-year-old') + ' dog: ' + dose.dose + ' for ' + dose.size + ' builds.');
    if (state.exposure === 'daily' || state.exposure === 'weekly') {
      bullets.push(dn() + '\'s regular exposure to parks, dogs and shared water means reinfection pressure — the maintenance phase is built in for exactly that.');
    }
    var c = state.primaryCluster;
    if (c === 'skin') bullets.push('The formula leads with gut-immune repair — the axis behind the skin and coat issues you flagged.');
    if (c === 'behavioral') bullets.push('Gut-flora rebuild is weighted up front — the serotonin pathway behind the restlessness you reported.');
    if (c === 'digestive') bullets.push('Gut-lining support is prioritised for the digestive signals you selected.');
    var meds = state.medications || [];
    if (meds.length && meds.indexOf('none') === -1) {
      bullets.push('It addresses the root cause the medication has been masking — instead of suppressing symptoms harder.');
    }
    bullets.push('90-day money-back guarantee: if ' + dn() + ' doesn\'t improve, you get a full refund.');
    return '<section class="plq-results-section"><div class="plq-inner">' +
      '<p class="plq-r-eyebrow">Made to measure</p>' +
      '<h2 class="plq-r-h2">Why this protocol fits ' + escapeHtml(dn()) + '</h2>' +
      '<ul class="plq-plus-list">' +
        bullets.map(function(b){ return '<li>' + escapeHtml(b) + '</li>'; }).join('') +
      '</ul>' +
    '</div></section>';
  }

  /* Ingredients — the 6-herb protocol, framed as composed for THIS dog.
     Each ingredient explains why it's in the formula for the answers the
     owner just gave (their primary cluster + reported symptoms). */
  function ingredientWhy(slot) {
    var c = state.primaryCluster;
    if (slot === 'walnut') {
      return 'The anti-parasitic core of ' + dn() + '\'s protocol — targets the load driving the symptoms you flagged.';
    }
    if (slot === 'pumpkin') {
      if (c === 'digestive') return 'Chosen for ' + dn() + '\'s digestive signals — soothes the irritated gut lining behind the scooting and grass eating.';
      if (c === 'skin') return 'Calms ' + dn() + '\'s gut lining — the layer that leaks the inflammation showing up on the skin.';
      return 'Soothes ' + dn() + '\'s gut lining while the cleanse does its work.';
    }
    /* probiotic */
    if (c === 'behavioral') return 'Rebuilds the gut flora that produces ' + dn() + '\'s serotonin — the pathway behind the restlessness you reported.';
    if (c === 'skin') return 'Restores the gut-immune balance behind ' + dn() + '\'s skin and coat issues.';
    if (c === 'senior') return 'Rebuilds the healthy flora ' + dn() + '\'s gut has been losing over the years.';
    return 'Rebuilds ' + dn() + '\'s healthy gut flora after the cleanse.';
  }

  function ingredientsHtml() {
    var slots = ['walnut', 'pumpkin', 'probiotic'];
    var groups = [
      { tag: 'Active cleanse', right: 'the base of the protocol' },
      { tag: 'Gut support', right: 'soothes while it works' },
      { tag: 'Rebuild', right: 'restores after the cleanse' }
    ];
    var items = t('results.ingredients', [
      { image: '', name: 'Black Walnut + Wormwood', desc: 'The traditional anti-parasitic duo — targets the load in the gut.' },
      { image: '', name: 'Pumpkin Seed + Oregano', desc: 'Soothes the gut lining and supports healthy digestion.' },
      { image: '', name: 'Veterinary Probiotic', desc: 'Rebuilds healthy gut flora after the cleanse.' }
    ]);
    if (!items || !items.length) return '';

    /* Personalised intro line, PureBob's "Voor asd hebben we…" pattern */
    var reasons = [];
    if (seniorAge()) reasons.push('age');
    if (state.primaryCluster === 'skin') reasons.push('skin and coat signals');
    if (state.primaryCluster === 'behavioral') reasons.push('restlessness');
    if (state.primaryCluster === 'digestive') reasons.push('digestive signals');
    if ((state.exposure === 'daily' || state.exposure === 'weekly')) reasons.push('regular exposure');
    var intro = 'For ' + dn() + ' we composed a targeted 21-day protocol, taking ' +
      (reasons.length ? ('their ' + reasons.slice(0, 2).join(' and ')) : 'their symptom profile') +
      ' into account. Every part is in there to support ' + dn() + ' optimally.';

    return '<section class="plq-results-section"><div class="plq-inner">' +
      '<p class="plq-r-eyebrow">' + escapeHtml(tpl(t('results.ingLabel', 'Inside the formula'))) + '</p>' +
      '<h2 class="plq-r-h2">' + escapeHtml(dn()) + '\'s protocol</h2>' +
      '<div class="plq-proto-intro">' + escapeHtml(intro) + '</div>' +
      items.map(function(it, i){
        var g = groups[i] || groups[2];
        var why = ingredientWhy(slots[i] || 'probiotic');
        return '<div class="plq-proto-group">' +
          '<div class="plq-proto-head">' +
            '<span class="plq-proto-tag"><span class="plq-proto-dot"></span>' + escapeHtml(g.tag) + '</span>' +
            '<span class="plq-proto-right">' + escapeHtml(g.right) + '</span>' +
          '</div>' +
          '<div class="plq-ing-card">' +
            (it.image ? '<div class="plq-ing-img"><img src="' + escapeHtml(it.image) + '" alt=""/></div>' : '') +
            '<div class="plq-ing-body">' +
              '<h4>' + escapeHtml(it.name || '') + '</h4>' +
              '<p>' + escapeHtml(it.desc || '') + '</p>' +
              '<p class="plq-ing-why"><span class="plq-ing-tick">✓</span>' + escapeHtml(why) + '</p>' +
            '</div>' +
          '</div>' +
        '</div>';
      }).join('') +
    '</div></section>';
  }

  /* Guarantee strip */
  function guaranteeHtml() {
    var title = tpl(t('results.guaranteeTitle', '90-day money-back guarantee'));
    var body = tpl(t('results.guaranteeBody', "Give the protocol a fair run. If you don't see a difference in {dogName}, email us for a full refund — keep the bottle."));
    return '<section class="plq-results-section"><div class="plq-inner">' +
      '<div class="plq-guarantee">' +
        '<div class="plq-guarantee-badge"><svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg></div>' +
        '<div><h3>' + escapeHtml(title) + '</h3><p>' + escapeHtml(body) + '</p></div>' +
      '</div>' +
    '</div></section>';
  }

  /* FAQ accordion */
  function faqHtml() {
    var items = t('results.faq', [
      { q: 'How do I give it to {dogName}?', a: 'Add the drops straight onto food once a day. The duck flavor means most dogs take it without noticing.' },
      { q: 'How fast will I see results?', a: 'Most owners notice the first changes within the first week, with the fullest effect by week 3 of the protocol.' },
      { q: 'Is it safe alongside other medication?', a: 'The formula is gentle and food-based. If {dogName} is on prescription medication, check with your vet first.' },
      { q: 'What if it doesn\'t work for {dogName}?', a: 'You\'re covered by a 90-day money-back guarantee — full refund, no need to return the bottle.' }
    ]);
    if (!items || !items.length) return '';
    return '<section class="plq-results-section plq-white"><div class="plq-inner">' +
      '<p class="plq-r-eyebrow">' + escapeHtml(tpl(t('results.faqLabel', 'Common questions'))) + '</p>' +
      '<div class="plq-faq">' +
        items.map(function(it, i){
          return '<div class="plq-faq-item" data-faq="' + i + '">' +
            '<button type="button" class="plq-faq-q"><span>' + escapeHtml(tpl(it.q || '')) + '</span><span class="plq-faq-icon" aria-hidden="true"></span></button>' +
            '<div class="plq-faq-a"><p>' + escapeHtml(tpl(it.a || '')) + '</p></div>' +
          '</div>';
        }).join('') +
      '</div>' +
    '</div></section>';
  }

  function renderResults() {
    recomputeDerived();
    var n = escapeHtml(dn());
    var dose = doseForSize();
    var bundle = bundleData();
    var symptoms = selectedSymptomsList();
    var tierLabels = ['LOW', 'MODERATE', 'HIGH'];
    var tierIdx = state.riskTier === 'low' ? 0 : state.riskTier === 'moderate' ? 1 : 2;

    var bundleHtml = renderBundleCard(bundle);

    var summaryTpl = t('results.summary', "Based on {dogName}'s answers, they are showing <strong>{count} of the 8 primary clinical indicators</strong> Dr. Kovač uses to assess for subclinical parasite load in adult dogs.");
    /* summaryTpl is intentionally HTML (merchant richtext with <strong>),
       so the user-typed dog name must be escaped before injection. */
    var summary = summaryTpl
      .replace(/\{dogName\}/gi, escapeHtml(dn()))
      .replace(/\{count\}/g, Number(state.symptomCount) || 0);
    var timeline = t('results.timeline', [
      { week: 'Week 1', body: 'First visible changes — scooting reduces, stool firms up, better sleep.' },
      { week: 'Week 2', body: 'Paw licking decreases, indoor accidents resolve, reactivity starts calming.' },
      { week: 'Week 3', body: 'Full symptom resolution for most dogs, coat quality improves, behavior stabilizes.' }
    ]);
    var stats = t('results.stats', [
      { num: '85%', lbl: 'of U.S. dog parks test positive for parasites (DOGPARCS 2020)' },
      { num: '75%', lbl: 'miss rate of Giardia on standard fecal tests' },
      { num: '90-day', lbl: "money-back guarantee if it doesn't work for {dogName}" }
    ]);

    var heroImg = t('results.heroImage', '');
    SCREEN_EL.innerHTML = [
      /* 1. Hero — split recap card: answers on the left, brand image right */
      '<section class="plq-results-section plq-r-hero"><div class="plq-inner">',
        '<div class="plq-hero-split' + (heroImg ? ' plq-has-img' : '') + '">',
          '<div class="plq-hero-left">',
            '<p class="plq-r-eyebrow">' + escapeHtml(tpl(t('results.eyebrow', 'Assessment Results'))) + '</p>',
            '<h1 class="plq-r-headline">' + riskHeadline() + '</h1>',
            severityMeterHtml(tierLabels, tierIdx),
            profileChipsHtml(),
          '</div>',
          heroImg ? '<div class="plq-hero-right"><img src="' + escapeHtml(heroImg) + '" alt="" onerror="var p=this.closest(\'.plq-hero-split\'); if(p){p.classList.remove(\'plq-has-img\');} this.parentNode.removeChild(this);"/></div>' : '',
        '</div>',
        '<p class="plq-r-summary">' + summary + '</p>',
        symptoms.length ? (
          '<div class="plq-r-symptoms">' +
            '<p class="plq-r-symptoms-label">' + n + '\'s specific symptom pattern</p>' +
            '<ul>' + symptoms.map(function(s){ return '<li>' + escapeHtml(s) + '</li>'; }).join('') + '</ul>' +
          '</div>'
        ) : '',
      '</div></section>',

      /* 2. Health signal tiles — clean per-concern callouts */
      healthTilesHtml(),

      /* 3. The protocol ("Het recept") — intro + grouped ingredients */
      ingredientsHtml(),

      /* 4. Why this protocol fits {dog} — plus-bullet recap */
      whyPerfectHtml(),

      /* 5. The plan — Dr. Kovač + recommendation quote + product card */
      '<section class="plq-results-section"><div class="plq-inner">',
        '<p class="plq-r-eyebrow">' + escapeHtml(tpl(t('results.recLabel', "Dr. Lena Kovač's Recommendation for {dogName}"))) + '</p>',
        renderDoctorCard(),
        '<div class="plq-r-rec">',
          '<p class="plq-r-rec-quote">' + escapeHtml(recommendationHtml()) + '</p>',
          '<p class="plq-r-dose">' + escapeHtml(tpl(t('results.doseIntro', "Based on {dogName}'s size"))) + ' (<strong>' + dose.size + '</strong>), the recommended dose is <strong>' + dose.dose + '</strong>, mixed directly into food.</p>',
        '</div>',
        bundleHtml,
      '</div></section>',

      /* 6. What\'s in the box */
      whatsInBoxHtml(),

      /* 7. Timeline — what to expect */
      '<section class="plq-results-section"><div class="plq-inner">',
        '<p class="plq-r-eyebrow">' + escapeHtml(tpl(t('results.timelineLabel', 'What to Expect on the Protocol'))) + '</p>',
        '<div class="plq-timeline">',
          timeline.map(function(tl, i){
            return '<div class="plq-timeline-card">' +
              (tl.image ? '<div class="plq-tl-img"><img src="' + escapeHtml(tl.image) + '" alt=""/></div>' : '') +
              '<h4><span class="plq-tl-n">' + (i + 1) + '</span>' + escapeHtml(tpl(tl.week || '')) + '</h4>' +
              '<p>' + escapeHtml(tpl(tl.body || '')) + '</p>' +
            '</div>';
          }).join(''),
        '</div>',
      '</div></section>',

      /* 8. Reviews — dogs like {dog} */
      '<section class="plq-results-section"><div class="plq-inner">',
        '<p class="plq-r-eyebrow">Reviews</p>',
        '<h2 class="plq-r-h2">Dogs like ' + n + ' love the protocol</h2>',
        clusterTestimonialsHtml(),
      '</div></section>',

      /* 8b. Guarantee strip */
      guaranteeHtml(),

      /* 7. Final CTA */
      '<section class="plq-results-section"><div class="plq-inner">',
        '<div class="plq-final">',
          '<h2>' + escapeHtml(tpl(t('final.title', 'Give {dogName} Their Best Shot at Full Recovery'))) + '</h2>',
          '<p>' + escapeHtml(tpl(t('final.body', "Every week you wait, the parasite load compounds. The gut damage accumulates. The medications stop working harder. Don't let {dogName} become another dog who's been \"managed\" for years."))) + '</p>',
          '<button type="button" class="plq-cta" data-bundle-cta="final">' + escapeHtml(tpl(t('final.cta', "Start {dogName}'s Protocol Today →"))) + '</button>',
          '<ul>',
            '<li>' + escapeHtml(tpl(t('final.bullet1', "90-day money-back guarantee — full refund if it doesn't work"))) + '</li>',
            '<li>' + escapeHtml(tpl(t('final.bullet2', 'Free shipping on orders over €54'))) + '</li>',
            '<li>' + escapeHtml(tpl(t('final.bullet3', 'Ships within 24 hours'))) + '</li>',
          '</ul>',
        '</div>',
      '</div></section>',

      /* 7b. FAQ (email is captured at the pre-results gate now) */
      faqHtml(),

      /* Sticky mobile ATC */
      '<div class="plq-sticky" id="plq-sticky"><button type="button" class="plq-cta" data-bundle-cta="sticky">' + escapeHtml(tpl(t('stickyCta', "Start {dogName}'s Protocol →"))) + '</button></div>'
    ].join('');

    /* Wire up clicks */
    SCREEN_EL.querySelectorAll('[data-bundle-cta]').forEach(function(btn){
      btn.addEventListener('click', function(){
        track('quiz_bundle_clicked', { bundle: recommendedBundle(), source_screen: btn.dataset.bundleCta });
        goToProduct();
      });
    });

    /* Severity pin entrance is a pure-CSS keyframe (plqPinIn) — no JS. */

    /* FAQ accordion */
    SCREEN_EL.querySelectorAll('.plq-faq-item').forEach(function(item){
      var btn = item.querySelector('.plq-faq-q');
      if (!btn) return;
      btn.addEventListener('click', function(){
        item.classList.toggle('plq-faq-open');
      });
    });

    /* Floating sticky CTA (all viewports, PureBob-style): visible from the
       moment results render, hiding only while the in-page product card is
       on screen so the two CTAs never stack. */
    var sticky = document.getElementById('plq-sticky');
    if (sticky) {
      var firstBundle = SCREEN_EL.querySelector('.plq-bundle');
      var finalBlock = SCREEN_EL.querySelector('.plq-final');
      var vis = new Map();
      var io = new IntersectionObserver(function(entries){
        entries.forEach(function(en){ vis.set(en.target, en.isIntersecting); });
        var anyVisible = false;
        vis.forEach(function(v){ if (v) anyVisible = true; });
        sticky.classList.toggle('plq-on', !anyVisible);
      }, { threshold: .2 });
      if (firstBundle) io.observe(firstBundle);
      if (finalBlock) io.observe(finalBlock);
      sticky.classList.add('plq-on');
    }

    clearSaved();
  }

  function renderDoctorCard() {
    var img = t('doctor.image', '');
    var name = t('doctor.name', 'Dr. Lena Kovač');
    var creds = t('doctor.credentials', 'DVM · Integrative Veterinary Medicine');
    var verified = t('doctor.verified', 'Verified Veterinarian');
    if (!img && !name) return '';
    return '<div class="plq-doctor">' +
      '<div class="plq-doctor-photo">' +
        '<div class="plq-doctor-photo-inner">' +
          (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(name) + '"/>' : '<span class="plq-doctor-init">' + escapeHtml((name || 'Dr').charAt(0)) + '</span>') +
        '</div>' +
        '<span class="plq-doctor-check" aria-label="Verified" title="Verified">' +
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
        '</span>' +
      '</div>' +
      '<div class="plq-doctor-info">' +
        '<div class="plq-doctor-name">' + escapeHtml(name) + '</div>' +
        '<div class="plq-doctor-creds">' + escapeHtml(creds) + '</div>' +
        '<div class="plq-doctor-verified"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 7.7l5.4-.8z"/></svg> ' + escapeHtml(verified) + '</div>' +
      '</div>' +
    '</div>';
  }

  function renderBundleCard(b) {
    var n = escapeHtml(dn());
    var ctaLabel = tpl(t('bundleCta', 'See Pawsy Parasite Cleanse Drops →'));
    return '<div class="plq-bundle">' +
      '<span class="plq-bundle-badge">Dr. Kovač recommends for ' + n + '</span>' +
      '<div class="plq-bundle-body">' +
        '<div class="plq-bundle-img">' + (b.image ? '<img src="' + escapeHtml(b.image) + '" alt="Pawsy Parasite Cleanse Drops"/>' : pawSvg('#377359')) + '</div>' +
        '<div>' +
          '<h3 class="plq-bundle-title">' + escapeHtml(b.title) + '</h3>' +
          (b.subtitle ? '<p class="plq-bundle-sub">' + escapeHtml(b.subtitle) + '</p>' : '') +
          '<ul class="plq-bundle-list">' +
            b.list.map(function(x){ return '<li>' + escapeHtml(x) + '</li>'; }).join('') +
          '</ul>' +
          '<button type="button" class="plq-cta" data-bundle-cta="recommended">' + escapeHtml(ctaLabel) + '</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }

  function clusterTestimonialsHtml() {
    var c = state.primaryCluster;
    var picks;
    if (c === 'skin') {
      picks = [
        { q: 'Bella had raw paws since she was a puppy and 2+ years on Apoquel. Four weeks on the Pawsy protocol and the licking stopped cold. Her vet wanted to know what changed.', a: 'Jennifer, French Bulldog, age 5' },
        { q: "Chronic ear infections for years — antibiotics every 6 weeks. Two months in and we haven't been back to the vet once. His coat is better than it's been in 5 years.", a: 'Mark, Cocker Spaniel, age 7' }
      ];
    } else if (c === 'behavioral') {
      picks = [
        { q: "My rescue used to pace at 2AM every single night. Didn't connect it to his gut until I read this. Six weeks on Pawsy and we both finally sleep through the night.", a: 'Danielle, Pitbull mix, age 3' },
        { q: 'The reactivity on walks was getting worse, not better, with training. I was skeptical but three weeks in and the improvement was undeniable. Training actually sticks now.', a: 'Chris, German Shepherd, age 4' }
      ];
    } else if (c === 'digestive') {
      picks = [
        { q: 'The scooting had been going on for a year. Vet kept expressing anal glands and blaming diet. Four weeks on Pawsy and it\'s completely gone. Wish I\'d known.', a: 'Amanda, Golden Retriever, age 6' },
        { q: 'Indoor accidents in my house-trained senior dog were breaking my heart. Week 3 on the protocol they just stopped. I\'m in tears.', a: 'Patricia, Lab mix, age 10' }
      ];
    } else {
      picks = [
        { q: "Everything improved at once — the scratching, the pacing, the digestion. I didn't realize how connected it all was until it all resolved together.", a: 'Rebecca, Cavalier King Charles Spaniel, age 5' },
        { q: "After years of stacking meds, my dog is finally off Apoquel and sleeping through the night. This is the first thing that's worked.", a: 'Tyler, Labrador, age 6' }
      ];
    }
    /* Two image slots from CFG that apply to whichever cluster pair shows */
    var img1 = t('clusterReviewImage1', '');
    var img2 = t('clusterReviewImage2', '');
    var imgs = [img1, img2];
    return '<div class="plq-sp-scroller" style="margin-top:24px;"><div class="plq-sp-grid">' +
      picks.map(function(p, i){
        var image = imgs[i] || '';
        return '<div class="plq-sp-card">' +
          (image ? '<div class="plq-sp-img"><img src="' + escapeHtml(image) + '" alt=""/></div>' : '') +
          '<div class="plq-sp-stars">★★★★★</div>' +
          '<p class="plq-sp-quote">' + escapeHtml(p.q) + '</p>' +
          '<p class="plq-sp-author">— ' + escapeHtml(p.a) + '</p>' +
        '</div>';
      }).join('') +
    '</div></div>';
  }

  function goToProduct() {
    var url = PRODUCT_URL;
    var sep = url.indexOf('?') > -1 ? '&' : '?';
    url += sep + 'quiz_tier=' + encodeURIComponent(state.riskTier);
    url += '&quiz_cluster=' + encodeURIComponent(state.primaryCluster);
    url += '&quiz_dog=' + encodeURIComponent(state.dogName);
    url += '&quiz_size=' + encodeURIComponent(state.dogSize);
    window.location.href = url;
  }

  /* ── BOOT ── */
  var QUIZ_STARTED = false;

  function boot() {
    renderShell();

    /* Back/forward browser */
    window.addEventListener('popstate', function(e){
      var hash = (location.hash || '').replace('#','');
      if (!hash) return;
      if (hash === currentScreenId) return;
      var histIdx = state.screenHistory.indexOf(hash);
      if (histIdx !== -1) {
        state.screenHistory = state.screenHistory.slice(0, histIdx + 1);
        currentScreenId = hash;
        renderScreen(hash);
        updateHeader(hash);
      }
    });

    /* Listen for handoff from intro section */
    window.addEventListener('plq:start', function(e){
      var detail = (e && e.detail) || {};
      startQuiz(detail);
    });

    /* If no intro on page, auto-start (backward compat / direct link) */
    var hasIntro = document.getElementById('pawsy-quiz-intro');
    if (!hasIntro) {
      startQuiz({});
    }
  }

  function startQuiz(detail) {
    if (QUIZ_STARTED) return;
    QUIZ_STARTED = true;

    /* Reveal engine */
    ROOT.classList.remove('plq-boot-hidden');
    document.body.classList.add('plq-quiz-active');

    /* Apply starter answer from intro if provided (pre-fills q5 primary concerns) */
    if (detail && detail.starterAnswer) {
      state.primaryConcerns = [detail.starterAnswer];
      save();
    }

    /* Resume? */
    var prior = load();
    if (prior && prior.dogName) {
      showResumePrompt(prior);
    } else {
      startFresh();
    }

    /* Scroll engine into view */
    setTimeout(scrollToQuiz, 60);
  }

  function showResumePrompt(prior) {
    var prompt = document.createElement('div');
    prompt.className = 'plq-resume';
    prompt.innerHTML = [
      '<div class="plq-resume-card">',
        '<h3>Continue where you left off with ' + escapeHtml(prior.dogName) + '?</h3>',
        '<p>We saved your progress from your last visit.</p>',
        '<div class="plq-resume-actions">',
          '<button class="plq-resume-yes" type="button">Yes, continue</button>',
          '<button class="plq-resume-no" type="button">Start over</button>',
        '</div>',
      '</div>'
    ].join('');
    ROOT.appendChild(prompt);
    prompt.querySelector('.plq-resume-yes').addEventListener('click', function(){
      state = Object.assign(state, prior);
      state.screenHistory = ['q1'];
      prompt.remove();
      var firstMissing = null;
      FLOW.forEach(function(id){
        if (firstMissing) return;
        if (QUESTIONS[id]) {
          var q = QUESTIONS[id];
          var v = state[q.key];
          var has = q.type === 'multi' ? (v && v.length > 0) : !!v;
          if (!has && shouldShow(id)) firstMissing = id;
        }
      });
      var resumeAt = firstMissing || 'q1';
      state.screenHistory = [resumeAt];
      go(resumeAt, { skipHistory: true });
    });
    prompt.querySelector('.plq-resume-no').addEventListener('click', function(){
      clearSaved();
      prompt.remove();
      startFresh();
    });
  }

  function startFresh() {
    state.screenHistory = ['q1'];
    go('q1', { skipHistory: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
